import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import type { Pagination as PaginationType } from '@/types'
import { cn } from '@/lib/utils'

interface PaginationProps {
  pagination: PaginationType
  baseUrl: string
  preserveParams?: Record<string, string | number | null | undefined>
}

export function Pagination({ pagination, baseUrl, preserveParams = {} }: PaginationProps) {
  const { page, pages, count, next, prev } = pagination

  if (pages <= 1) return null

  function buildUrl(pageNum: number) {
    const params = new URLSearchParams()

    Object.entries(preserveParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.set(key, String(value))
      }
    })

    if (pageNum > 1) {
      params.set('page', String(pageNum))
    }

    const queryString = params.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }

  const pageNumbers: (number | 'ellipsis')[] = []
  const showPages = 5

  if (pages <= showPages + 2) {
    for (let i = 1; i <= pages; i++) {
      pageNumbers.push(i)
    }
  } else {
    pageNumbers.push(1)

    let start = Math.max(2, page - Math.floor(showPages / 2))
    let end = Math.min(pages - 1, start + showPages - 1)

    if (end === pages - 1) {
      start = Math.max(2, end - showPages + 1)
    }

    if (start > 2) {
      pageNumbers.push('ellipsis')
    }

    for (let i = start; i <= end; i++) {
      pageNumbers.push(i)
    }

    if (end < pages - 1) {
      pageNumbers.push('ellipsis')
    }

    pageNumbers.push(pages)
  }

  return (
    <div className="flex items-center justify-between border-t border-border px-4 md:px-6 py-3">
      <p className="text-[13px] text-muted-foreground">
        <span className="font-medium text-foreground">{((page - 1) * pagination.limit) + 1}</span>
        {' - '}
        <span className="font-medium text-foreground">{Math.min(page * pagination.limit, count)}</span>
        {' of '}
        <span className="font-medium text-foreground">{count}</span>
      </p>

      <div className="flex items-center gap-1">
        <Link href={buildUrl(prev || 1)} preserveScroll>
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={!prev}
            className="disabled:opacity-30"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
        </Link>

        <div className="hidden md:flex items-center gap-0.5 mx-1">
          {pageNumbers.map((pageNum, idx) => {
            if (pageNum === 'ellipsis') {
              return (
                <span key={`ellipsis-${idx}`} className="px-1.5 text-muted-foreground text-xs">
                  ...
                </span>
              )
            }

            return (
              <Link key={pageNum} href={buildUrl(pageNum)} preserveScroll>
                <Button
                  variant={pageNum === page ? 'default' : 'ghost'}
                  size="sm"
                  className={cn(
                    'min-w-[32px] h-7 text-xs',
                    pageNum === page && 'pointer-events-none'
                  )}
                >
                  {pageNum}
                </Button>
              </Link>
            )
          })}
        </div>

        <Link href={buildUrl(next || pages)} preserveScroll>
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={!next}
            className="disabled:opacity-30"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  )
}
